import React from "react";
import { useTheme } from "@mui/material";

interface Props {
    width?: number;
}

/**
 * Illustration for the service-reminders empty state: a miniature "reminder card"
 * (header bar + a few list rows) with a bell glyph in front — mirroring the
 * theme-colored, hand-drawn style of {@link InstalledBaseEmptyIllustration}.
 *
 * Colors come from the application theme: the brand primary for the bell glyph,
 * neutral greys for the card and rows.
 */
const ServiceReminderEmptyIllustration = ({ width = 200 }: Props) => {
    const theme = useTheme();

    const backdrop = theme.palette.grey[100];
    const cardFill = theme.palette.common.white;
    const border = theme.palette.grey[300];
    const titleLine = theme.palette.grey[300];
    const subLine = theme.palette.grey[200];
    const primary = theme.palette.primary.main;
    const white = theme.palette.common.white;

    return (
        <svg
            width={width}
            viewBox="0 0 220 200"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            role="presentation"
            aria-hidden="true"
        >
            {/* soft backdrop */}
            <circle cx="110" cy="100" r="92" fill={backdrop} />

            {/* reminder card */}
            <rect x="60" y="42" width="100" height="116" rx="10" fill={cardFill} stroke={border} strokeWidth="2" />
            {/* header bar */}
            <rect x="60" y="42" width="100" height="22" rx="10" fill={subLine} />
            <rect x="60" y="56" width="100" height="8" fill={subLine} />

            {/* list rows: bullet + title + subline */}
            <g>
                <circle cx="76" cy="84" r="4" fill={border} />
                <rect x="86" y="80" width="48" height="6" rx="3" fill={titleLine} />
                <rect x="86" y="90" width="30" height="5" rx="2.5" fill={subLine} />

                <circle cx="76" cy="108" r="4" fill={border} />
                <rect x="86" y="104" width="48" height="6" rx="3" fill={titleLine} />
                <rect x="86" y="114" width="30" height="5" rx="2.5" fill={subLine} />

                <circle cx="76" cy="132" r="4" fill={border} />
                <rect x="86" y="128" width="40" height="6" rx="3" fill={titleLine} />
                <rect x="86" y="138" width="24" height="5" rx="2.5" fill={subLine} />
            </g>

            {/* halo so the glyph reads as being in front of the card */}
            <circle cx="150" cy="142" r="34" fill={white} />

            {/* bell glyph */}
            <g
                stroke={primary}
                strokeWidth="4.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
            >
                {/* bell body */}
                <path d="M150 121 c-9 0 -15 6 -15 15 c0 11 -4 15 -7 18 h44 c-3 -3 -7 -7 -7 -18 c0 -9 -6 -15 -15 -15 z" fill={primary} />
                {/* top knob */}
                <path d="M150 117 v4" />
                {/* clapper */}
                <path d="M144 154 a6 6 0 0 0 12 0" fill={white} stroke={primary} />
            </g>
        </svg>
    );
};

export default ServiceReminderEmptyIllustration;
